import { exists } from '../../UtilityHelpers'
import { ApiResponse } from '../../types/Response'
import { DissectedError } from '../../UseForm'
import { parseResponseOk } from './helpers/parseResponseOk'
import { parseApiResponseJson } from './helpers/parseApiResponseJson'
import { parseApiResponseValidationErrors } from './helpers/parseValidationErrors'
import { parseApiResponseIsSuccess } from './helpers/parseApiResponseIsSuccess'
import { mergeApiResponses } from './helpers/mergeApiResponses'
import { defaultDissectedErrorHandler } from './helpers/defaultErrorHandler'
import { DissectParserProps } from './types/DissectParserTypes'
import { parseApiResponseExpectData } from './helpers/parseApiResponseExpectData'
import { log } from '../../Helpers/logging'
import { HandleErrorCallback } from './types/HandleErrorCallback'
import { logRequestDetails } from './helpers/logRequestDetails'
import { rethrowErrors } from './helpers/rethrowErrors'
import { safeAwaitApiResponse } from './helpers/safeAwaitApiResponse'

export interface DissectResponseProps {
    /** The response to be dissected */
    response: Response
    /** original request */
    request: Request | undefined
    /** 
     * The request body 
     * @TODO in the future this should be required, or (when not available) consciously omitted by the caller.
    */
    requestBody?: any
    /** The name of the function that called dissectResponse */
    caller: string
    /** IMPORTANT: only provide formId in combination with our own useForm system. When formId is provided, the response will be auto-thrown when apiResponse.isSuccess is false.
     * If a request is linked to a form, provide the form id here. This will be used in defaultErrorHandling, and is made available to customErrorHandling*/
    formId?: string
    /** If true and response.isSuccess is true, throws an error when apiResponse.data is null | undefined */
    expectData?: boolean
    /** If true, throws a HandledApplicationError when apiResponse.isSuccess is false or response.ok is false, even when no formId was provided */
    throwWhenNotSuccessful?: boolean
    /* overrides default error handling. dissectedParts is an array of promise items. Every promise item may throw a DissectedError. For each thrown error, decide what needs to be done */
    customErrorHandling?: (dissectedParts: Promise<void>) => Promise<void>
}

interface DissectResponseReturnValue<TApiResponse> {
    defaultErrorHandling: () => Promise<TApiResponse>
    customErrorHandling: (handler: HandleErrorCallback) => Promise<TApiResponse>
}

/**
 * Parses and validates a fetch Response, returning an object with error handling methods.
 *
 * Runs multiple validation parsers (response.ok, JSON parsing, validation errors, isSuccess, expectData)
 * and collects any errors. Returns an object with two methods:
 * - `defaultErrorHandling()`: Uses built-in error handling (logs errors, throws HandledApplicationError when formId is provided with validation errors)
 * - `customErrorHandling(handler)`: Uses a custom HandleErrorCallback for each DissectedError
 *
 * Both methods return the merged API response, augmented with any modifications from error handlers.
 *
 * @throws HandledApplicationError - When using defaultErrorHandling with a formId and validation errors occur
 * @throws Redirect errors - Next.js redirect errors are always rethrown
 */
export const dissectResponse = async <TApiResponse extends ApiResponse = ApiResponse>(
    props: DissectResponseProps,
): Promise<DissectResponseReturnValue<TApiResponse>> => {
    const apiResponse = props.response.json()
    const awaitedApiResponse: TApiResponse = await safeAwaitApiResponse(apiResponse) // may be an empty object if json parsing fails

    const parseResponseProps: DissectParserProps<TApiResponse> = {
        responseBody: apiResponse,
        responseStatus: props.response.status,
        responseStatusText: props.response.statusText,
        responseOk: props.response.ok,
        ...props,
    }

    const dissected: Promise<void>[] = [
        parseResponseOk(parseResponseProps),
        parseApiResponseJson<TApiResponse>(parseResponseProps),
        parseApiResponseValidationErrors(parseResponseProps),
        parseApiResponseIsSuccess(parseResponseProps),
        parseApiResponseExpectData(parseResponseProps),
    ]

    const processDissectedPart = async (handler: HandleErrorCallback | undefined, dissectedPart: Promise<void>) => {
        try {
            await dissectedPart
            return awaitedApiResponse
        } catch (error) {
            logRequestDetails(props.request, props.requestBody)
            if (error instanceof DissectedError) {
                const result = handler
                    ? await handler({
                        error,
                        apiResponseState: awaitedApiResponse,
                        formId: props.formId,
                        throwWhenNotSuccessful: props.throwWhenNotSuccessful,
                    })
                    : await defaultDissectedErrorHandler({
                        error,
                        apiResponseState: awaitedApiResponse,
                        formId: props.formId,
                        throwWhenNotSuccessful: props.throwWhenNotSuccessful,
                    })
                if (exists(result)) {
                    return result
                }
            }
            return awaitedApiResponse
        }
    }

    const errorHandling = async (handler?: HandleErrorCallback): Promise<TApiResponse> => {
        try {
            const processPart = processDissectedPart.bind(undefined, handler)
            const augmentedApiResponsesHandled = dissected.map(processPart)

            const augmentedApiResponses = await Promise.all(augmentedApiResponsesHandled)

            if (augmentedApiResponses.length === 0) {
                log('no augmented api responses, returning initial api response', props.caller)
            }

            // insert initial apiResponse at the beginning to ensure all properties are present
            augmentedApiResponses.unshift(await apiResponse)

            return await mergeApiResponses(augmentedApiResponses.filter(exists) as TApiResponse[])
        } catch (error) {
            rethrowErrors(error)
            return awaitedApiResponse
        }
    }

    const customErrorHandling = async (handler: HandleErrorCallback): Promise<TApiResponse> => {
        return errorHandling(handler)
    }

    return {
        defaultErrorHandling: errorHandling,
        customErrorHandling,
    }
}
